import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import pluralize from "pluralize";

import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { RiCheckboxCircleLine, RiErrorWarningLine } from "@remixicon/react";

import { ShippingDialogProps } from "../../../types/Shipping";
import BaseShippingDialog from "./BaseShippingDialog";
import {
  ValidationError_T,
  hasInvalidAppleCustomerSelection,
  hasExportDateOutsideShippingWeek,
  getMissingDestinations,
} from "../../../utils/shippingManagementValidators";
import useApi from "../../../hooks/useGaneshApi";
import { formatDateWithWeekday } from "utils/formatting";

const CreateShipmentDialog: React.FC<ShippingDialogProps> = (props) => {
  const theme = useTheme();
  const { apiRequest } = useApi();

  const { selectedData } = props;
  const { fullySelectedGroups, partiallySelectedGroups } = selectedData;

  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState<ValidationError_T[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const selectedGroups = [...fullySelectedGroups, ...partiallySelectedGroups];

  const targetDate =
    selectedGroups[0]?.shipments[0]?.target_fulfillment_date || "";

  // format date for display only
  const formattedDate = formatDateWithWeekday(targetDate);

  // hard stops
  const invalidSelectionHardStop = hasInvalidAppleCustomerSelection(
    props.customerId,
    partiallySelectedGroups
  );

  // soft stops
  const showExportDateWarning = hasExportDateOutsideShippingWeek(
    targetDate,
    selectedGroups
  );
  const missingDestinationUriItems = getMissingDestinations(selectedGroups);

  const { mutateAsync: createFulfillmentGroupMutation, isPending } =
    useMutation({
      mutationFn: async () => {
        setProgress(0);
        setErrors([]);
        setIsComplete(false);

        for (let i = 0; i < selectedGroups.length; i++) {
          const group = selectedGroups[i];
          try {
            const response = await apiRequest<{ success: boolean }>(
              "/v1/fulfillments/shipments",
              {
                method: "POST",
                body: {
                  fulfillment_group_id: group.fulfillmentGroupId,
                  fulfillment_date: targetDate,
                },
              }
            );

            if (!response.data?.success) {
              throw new Error(response.error || "Unknown error");
            }
          } catch (error) {
            setErrors((prev) => [
              ...prev,
              {
                fulfillment_group_id: group.fulfillmentGroupId,
                name: group.name,
                error: error instanceof Error ? error.message : "Unknown error",
              },
            ]);
          }
          setProgress(((i + 1) / selectedGroups.length) * 100);
        }

        return true;
      },
      onSuccess: () => {
        setIsComplete(true);
      },
      onError: () => {
        setIsComplete(true);
      },
    });

  const handleSubmit = () => {
    createFulfillmentGroupMutation();
  };

  return (
    <BaseShippingDialog
      {...props}
      title="Create Shipment"
      onSubmit={handleSubmit}
      submitText={isPending ? "Creating..." : "Create"}
      submitDisabled={
        selectedGroups.length === 0 ||
        isPending ||
        isComplete ||
        invalidSelectionHardStop
      }
    >
      <Box>
        {invalidSelectionHardStop && (
          <Alert
            severity="error"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.error.main }}
              />
              <Typography variant="body2">
                Error: For this customer, fulfillment groups must be selected to
                proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidSelectionHardStop && selectedGroups.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine size={20} color="warning.main" />
              <Typography variant="body2">
                Please select at least one fulfillment group to proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidSelectionHardStop && showExportDateWarning && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.warning.main }}
              />
              <Typography variant="body2">
                Warning: Some exports are not within the current shipping week.
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidSelectionHardStop && missingDestinationUriItems.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RiErrorWarningLine
                  size={20}
                  style={{ color: theme.palette.warning.main }}
                />
                <Typography variant="body2">
                  Warning: Some deliveries have missing destination URIs:
                </Typography>
              </Box>
            </Alert>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {missingDestinationUriItems.map((item) => (
                <Chip
                  key={`${item.fulfillmentGroupId}-${item.deliveryId}`}
                  label={`${item.groupName} - ${item.deliveryName}`}
                />
              ))}
            </Box>
          </>
        )}

        {!invalidSelectionHardStop && selectedGroups.length > 0 && (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Create shipments from {selectedGroups.length}{" "}
              {pluralize("fulfillment group", selectedGroups.length)}?
            </Typography>

            <Box sx={{ mb: 2 }}>
              <TextField
                type="text"
                size="small"
                fullWidth
                disabled
                value={formattedDate}
                label="Target Fulfillment Date"
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {(isPending || isComplete) && (
              <Box sx={{ width: "100%", mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Processed: {Math.round(progress)}% (
                  {Math.floor((progress / 100) * selectedGroups.length)} of{" "}
                  {selectedGroups.length})
                </Typography>
              </Box>
            )}

            {isComplete && errors.length === 0 && (
              <Alert severity="success" sx={{ mt: 2 }} icon={false}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RiCheckboxCircleLine size={20} color="success.main" />
                  <Typography variant="body2">
                    Successfully created shipments for all{" "}
                    {selectedGroups.length}{" "}
                    {pluralize("fulfillment group", selectedGroups.length)}.
                  </Typography>
                </Box>
              </Alert>
            )}

            {isComplete && errors.length > 0 && (
              <>
                <Alert severity="error" sx={{ mt: 2 }} icon={false}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RiErrorWarningLine size={20} color="error.main" />
                    <Typography variant="body2">
                      Failed to create shipments for {errors.length}{" "}
                      {pluralize("fulfillment group", errors.length)}:
                    </Typography>
                  </Box>
                </Alert>
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  {errors.map((error) => (
                    <li key={error.fulfillment_group_id}>
                      {error.name} (ID: {error.fulfillment_group_id})
                      {error.error && `: ${error.error}`}
                    </li>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </BaseShippingDialog>
  );
};

export default CreateShipmentDialog;
